// owner: social-graph domain agent (W2 imports)
import { z } from 'zod';
import { EpochMsSchema, PaginationQuerySchema, UlidSchema, paginated } from './common';

export const ImportMatchedBySchema = z.enum(['email', 'name_company']).nullable();
export type ImportMatchedBy = z.infer<typeof ImportMatchedBySchema>;

export const ImportBatchStatusSchema = z.enum(['previewed', 'committed']);
export type ImportBatchStatus = z.infer<typeof ImportBatchStatusSchema>;

export const ImportRowResultSchema = z.object({
  contactId: UlidSchema,
  firstName: z.string(),
  lastName: z.string(),
  company: z.string(),
  matchedUserId: UlidSchema.nullable(),
  matchedBy: ImportMatchedBySchema,
});
export type ImportRowResult = z.infer<typeof ImportRowResultSchema>;

export const ImportUploadResponseSchema = z.object({
  importId: UlidSchema,
  rows: z.array(ImportRowResultSchema),
});
export type ImportUploadResponse = z.infer<typeof ImportUploadResponseSchema>;

export const ImportCommitSchema = z.object({
  /** Subset of this import's matched userIds to send connection requests to. Default: all matched. */
  connectUserIds: z.array(UlidSchema).optional(),
});
export type ImportCommit = z.infer<typeof ImportCommitSchema>;

export const ImportCommitResponseSchema = z.object({
  importId: UlidSchema,
  connected: z.number().int().nonnegative(),
  invited: z.number().int().nonnegative(),
  invitesSkipped: z.number().int().nonnegative(),
  /**
   * Review E N1-02: agents are charged one daily-cap unit per connection request and per invite.
   * true = the cap was reached and the commit stopped early; the batch stays 'previewed' so it can
   * be committed again later (already-sent requests/invites are skipped). Always false for humans.
   */
  capReached: z.boolean(),
  /** Contacts not processed because the cap was reached (0 when capReached is false). */
  remaining: z.number().int().nonnegative(),
});
export type ImportCommitResponse = z.infer<typeof ImportCommitResponseSchema>;

export const ImportBatchSchema = z.object({
  id: UlidSchema,
  status: ImportBatchStatusSchema,
  createdAt: EpochMsSchema,
});
export type ImportBatch = z.infer<typeof ImportBatchSchema>;
export const ImportBatchListSchema = paginated(ImportBatchSchema);
export const ImportBatchListQuerySchema = PaginationQuerySchema;

export const ImportContactSchema = z.object({
  id: UlidSchema,
  firstName: z.string(),
  lastName: z.string(),
  url: z.string(),
  email: z.string(),
  company: z.string(),
  position: z.string(),
  connectedOn: z.string(),
  matchedUserId: UlidSchema.nullable(),
  matchedBy: ImportMatchedBySchema,
});
export type ImportContact = z.infer<typeof ImportContactSchema>;

export const ImportDetailSchema = z.object({
  batch: ImportBatchSchema,
  contacts: z.array(ImportContactSchema),
});
export type ImportDetail = z.infer<typeof ImportDetailSchema>;

export const ImportIdParamsSchema = z.object({ importId: UlidSchema });
